import {AfterViewInit, Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {QuotationService} from '../../services/quotation-service/quotation.service';
import {LeadService} from '../../services/lead-service/lead.service';
import {WebLeadType} from '../../model/leads/WebLeadType';
import {environment} from '../../../environments/environment';
import {ErrorMessages} from '../../model/ErrorMessages';
import {ValidationService} from '../../services/validation-service/validation.service';
import {ContentComponent} from '../content/content.component';
import {BehaviorSubject} from 'rxjs';
import {InsureStep} from '../insureStep';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {isNullOrUndefined} from 'util';
import {VehicleSearch} from '../../model/VehicleSearch';
import {VehicleColour} from '../../model/VehicleColour';
import {LicenseType} from '../../model/LicenseType';
import {GoogleAnalyticsService} from "google-analytics";

@Component({
  selector: 'app-more-vehicle-info',
  templateUrl: './more-vehicle-info.component.html',
  styleUrls: ['./more-vehicle-info.component.scss']
})
export class MoreVehicleInfoComponent extends InsureStep implements OnInit, AfterViewInit {
  public loading = false;
  public errorMessage = ErrorMessages;
  public searchTerm: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public vehicleList: BehaviorSubject<VehicleSearch[]> = new BehaviorSubject<VehicleSearch[]>([]);
  public searched = false;
  public getQuoteMessage = '';
  public contentNumberOfClaims: string = null;
  public contentLicenseType: string = null;
  public vehicleColours: VehicleColour[];
  public licenseTypes: LicenseType[];
  public loadingVehicleSearch = false;
  public vehicleColourChoice: VehicleColour = null;
  public licenseTypeChoice: LicenseType = null;

  constructor(router: Router,
              public quotationService: QuotationService,
              public validationService: ValidationService,
              private leadService: LeadService,
              private contentComponent: ContentComponent,
              private googleAnalyticsService: GoogleAnalyticsService,) {
    super(router, quotationService);
  }

  ngOnInit() {
    // TODO: to be used with vehicle search
    this.searchTerm.pipe(debounceTime(500),
      distinctUntilChanged())
      .subscribe(term => {
        this.vehicleSearch(term);
      });

    this.quotationService.getVehicleColours().subscribe((colourList: VehicleColour[]) => {
      this.vehicleColours = colourList;
    }, error1 => {
      this.quotationService.error = true;
    });

    this.quotationService.getLicenseTypes().subscribe((licenseList: LicenseType[]) => {
      this.licenseTypes = licenseList;
    }, error1 => {
      this.quotationService.error = true;
    });
  }

  continueToStep3() {
    // Handle the change of loading text
    this.getQuoteMessage = 'Few more seconds for us to prepare your quote...';
    setTimeout(() => {
      this.getQuoteMessage = 'Your personalized quote is being formulated... \n This can take up to 60 seconds';
    }, 5000);

    // update lead with user clicked get quote
    this.leadService.clickedGetAQuote = 'true';
    const leadUpdate = this.leadService.updateLead(WebLeadType.WEB_LEAD_3, null, null, null,
      this.quotationService.campaign, this.quotationService.quotationRequest.medium);
    if (leadUpdate) {
      leadUpdate.subscribe(
        response => {
          this.leadService.updateLeadResponse = response;
        }, error => {
          this.quotationService.error = true;
        });
    }

    this.validationService.clearErrors();
    if (this.validationService.isStepTwoAltValid()) {
      this.loading = true;

      this.quotationService.generateQuotes().subscribe((quotationResults) => {
        // Assign quotations
        this.quotationService.quotationsForClient.quickQuote[0] = quotationResults['quickQuote'][0];
        this.quotationService.quotationsForClient.quickQuote[1] = quotationResults['quickQuote'][2];
        this.quotationService.quotationsForClient.quickQuote[2] = quotationResults['quickQuote'][1];
        this.loading = false;
        this.goToStep('Step 3');

        let recommendedQuote = null;
        if (this.quotationService.quotationsForClient && this.quotationService.quotationsForClient.quickQuote) {
          const quotes = this.quotationService.quotationsForClient.quickQuote;
          const preferredQuote = this.findElementQuote(quotes, 'quoteType', this.quotationService.quotationType);
          if (preferredQuote) {
            recommendedQuote = preferredQuote.quoteNo;
          }
        }
        const leadUpdate = this.leadService.updateLead(WebLeadType.WEB_LEAD_2, recommendedQuote, null, null,
          this.quotationService.campaign, this.quotationService.quotationRequest.medium);
        if (leadUpdate) {
          leadUpdate.subscribe(
            response => {
              this.leadService.updateLeadResponse = response;
              // track lead update success with GA
              if(response['leadReferenceNumber']){
                this.googleAnalyticsService.postGaEvent(
                  'form_step_complete',
                  {
                    'form_name': 'insure_quote_tool',
                    'form_action': 'get_quote',
                    'form_step': 3
                  }
                );
              }
            }, error => {
              this.quotationService.error = true;
            });
        }
      }, error1 => {
        if (this.quotationService.parseError(error1)) {
          return;
        }
        this.getContentError(this.quotationService.errorCode)
          .subscribe((response) => {
            this.quotationService.error = true;
            this.quotationService.errorMessage = response;
          }, contentError => {
            this.quotationService.setFallbackError();
          });
      });
    }
  }

  getContentError(errorCode: string) {
    return this.contentComponent.customGetContent(environment.errorContentUrl + errorCode);
  }

  findElementQuote(arr, propName, propValue) {
    for (let i = 0; i < arr.length; i++) {
      if (arr[i][propName] === propValue && arr[i]['planType'] === 'CLASSIC_PLAN') {
        return arr[i];
      }
    }
  }

  detailsInvalid() {
    return this.loading || this.quotationService.quotationRequest.vehicleDetails == null;
  }

  public vehicleSearch(phrase: string): void {
    if (phrase.length >= 4) {
      // clean any special chars from the input
      phrase = phrase.replace(/[^a-zA-Z0-9\.\\\/\'\s\,]/g, '');

      this.loadingVehicleSearch = true;
      this.quotationService.modelRequest.model = phrase;
      this.quotationService.getVehicleSearchOptions().subscribe((vehicleList: VehicleSearch[]) => {
        this.searched = true;
        this.loadingVehicleSearch = false;
        this.vehicleList.next(vehicleList);
      }, error1 => {
        this.quotationService.error = true;
        this.quotationService.errorMessage = this.quotationService.fallBackError;
        this.quotationService.errorCode = null;
        this.loadingVehicleSearch = false;
      });
    }
  }

  public selectVehicle(vehicle: VehicleSearch) {
    if (!isNullOrUndefined(vehicle)) {
      this.quotationService.quotationRequest.vehicleDetails.modelRangeYearCode = vehicle.modelRangeYearCode;
      this.quotationService.quotationRequest.vehicleDetails.description = vehicle.description;
      this.validationService.clearErrors();
      this.validationService.vehicleSelectValue = vehicle.description;
    }
    this.vehicleList.next([]);
  }

  inputChange(value: any) {
    this.searchTerm.next(value);
  }

  ngAfterViewInit(): void {
    this.contentComponent.customGetContent('/discoverycoza/fragments/car-and-home-insurance/quotes/step2-claims-history-tool-tip')
      .subscribe((result) => {
        this.contentNumberOfClaims = result;
      });
    this.contentComponent.customGetContent('/discoverycoza/fragments/car-and-home-insurance/quotes/step2-licence-type-tool-tip')
      .subscribe((result) => {
        this.contentLicenseType = result;
      });
  }

  colourSelect() {
    this.quotationService.quotationRequest.vehicleDetails.colorDescr = this.vehicleColourChoice.description;
    this.quotationService.quotationRequest.vehicleDetails.colorCd = this.vehicleColourChoice.colorCode;
  }

  licenseSelect() {
    this.quotationService.quotationRequest.licenseDetails.licenseTypeCode = this.licenseTypeChoice.licenseTypeCode;
    this.quotationService.quotationRequest.licenseDetails.description = this.licenseTypeChoice.description;
  }
}
